/**
 * The subset of `resource/action` capability identifiers the web gates UI on,
 * mirroring the backend catalog
 * (`apps/api/src/modules/billing/resource-identifiers.types.ts`). These are the
 * strings `GET /billing/capabilities` returns and `ResourceControlGuard`
 * enforces; wrapping an entry point in `<LockedFeature>` keeps it VISIBLE and
 * locked when the plan doesn't grant the identifier, so the UI explains the 403
 * the API would return instead of silently deleting the control from the DOM.
 *
 * There is no shared package exporting these to the frontend, so this const is
 * the single web-side home — import from here rather than sprinkling string
 * literals across feature files. Keep it in sync with the backend catalog.
 */
export const CapabilityIdentifier = {
  API_KEYS_CREATE: 'api-keys/create',
  TAGS_MANAGE: 'tags/manage',
  SETTINGS_TEAM_INVITE: 'settings/team-invite',
  // The bulk-workspace pane gates on `dwd-workspace/create` — the identifier the
  // "add a bulk workspace" API route (`POST /dwd-workspace`) actually enforces —
  // NOT `dwd-workspace/connect`, which gates no backend route. This keeps the UI
  // gate matched to the 403 the API would return. Granted by trial + all paid
  // plans (starter/growth/scale); withheld from free.
  DWD_WORKSPACE_CREATE: 'dwd-workspace/create',
  // Per-section gates on the Email Warmup Settings page, granted on growth and
  // above (`starter`/`free` get none). Mirrored here to keep this const a
  // faithful copy of the backend catalog, but note the settings page does NOT
  // resolve them through `GET /billing/capabilities`: `GET /email-accounts/:id/
  // warmup-settings` already returns the resolved booleans
  // (`customEspAllowed` / `customContentAllowed` / `templatesAllowed` /
  // `customScheduleAllowed` / `customLanguageAllowed`) alongside the settings, so
  // the lock costs one fetch either way and stays consistent with the values it
  // gates. `useFeatureLock`'s `locked` override is what those sections pass.
  WARMUP_SETTING_ESP_PRIORITIZATION: 'warmup-setting/esp-prioritization',
  WARMUP_SETTING_CONTENT_SETTING: 'warmup-setting/content-setting',
  WARMUP_SETTING_SENDING_SCHEDULE: 'warmup-setting/sending-schedule',
  WARMUP_SETTING_WARMUP_LANGUAGE: 'warmup-setting/warmup-language',
  WARMUP_SETTING_TEMPLATE_COLLECTION: 'warmup-setting/template-collection',
  // Sixth warmup-settings section gate, added in Part B. Like its five siblings
  // it rides the warmup-settings response (`replyRateAllowed`) rather than
  // `GET /billing/capabilities` on the single-account page, and resolves through
  // capabilities on the bulk page. Locked on `free` only — every paid tier holds
  // it and is instead bounded by the NUMERIC `replyRateCap`, which is a limit,
  // not a capability, and never appears in this const.
  WARMUP_SETTING_REPLY_RATE: 'warmup-setting/reply-rate',

  // The per-account ESP report panels. Distinct from `reports/trends` and
  // `reports/deliverability` (the workspace-wide reports), which the web does
  // NOT gate on: those live on the dashboard behind their own feature flags and
  // have no <LockedFeature> call site today.
  WARMUP_REPORT_DELIVERABILITY_SCORE: 'warmup-report/deliverability-score',
  WARMUP_REPORT_ESP_ANALYTICS: 'warmup-report/esp-analytics',

  // CSV bulk connect. Granted on the same plans and roles as DWD_WORKSPACE_CREATE
  // — the two are the halves of one product decision ("can this org add
  // mailboxes in bulk?") and `free` loses both — but NOT while the subscription
  // is PAUSED (2026-09-18): it follows `email-account/connect` there, while
  // DWD_WORKSPACE_CREATE stays granted. Kept as two identifiers rather than one
  // because they gate two different routes on two different modules, and the
  // locked cards say different things.
  //
  // NOTE the PAUSED capability set is UX, never enforcement: every mutating
  // route is already refused by `SubscriptionPausedInterceptor` (`040006`)
  // whatever the capability rows say. Withholding an identifier while paused is
  // what makes the web render that surface LOCKED rather than letting the user
  // press a control that can only 403.
  EMAIL_ACCOUNT_BULK_CONNECT: 'email-account/bulk-connect',

  // The `/email-accounts` list's bulk "Export Report" popover
  // (`export/ExportReportPopover.tsx`). Backend route: `POST
  // /reports/warmup-export`, guarded by `ResourceIdentifier.WARMUP_REPORT_EXPORT`.
  // Granted identically to `warmup-report/esp-analytics` on every plan/status/
  // role (see `packages/database/src/seeds/plan-catalog.ts`'s
  // `withWarmupReportExportParity`).
  WARMUP_REPORT_EXPORT: 'warmup-report/export',
} as const;

export type CapabilityIdentifier =
  (typeof CapabilityIdentifier)[keyof typeof CapabilityIdentifier];

/** User-facing copy for one gated feature. */
export type CapabilityCopy = {
  /** What the feature is called in the UI — the locked card's heading. */
  label: string;
  /** One or two sentences on what the feature does, for the locked preview. */
  preview: string;
};

/**
 * The feature catalog behind every locked state: what each gated capability is
 * called and what it does.
 *
 * It lives WEB-SIDE by explicit decision. The backend's `features.description`
 * column holds internal boilerplate (`'Resource: create an API key'`), not
 * product copy, so there is nothing to source from the API — and slug-humanizing
 * the identifier (the old `humanize()` path) produces "Api keys create", which is
 * not an explanation of anything.
 *
 * `satisfies Record<CapabilityIdentifier, CapabilityCopy>` is load-bearing:
 * adding a member to {@link CapabilityIdentifier} without copy for it fails the
 * build, so a newly-gated feature can never ship a blank lock card.
 */
export const CAPABILITY_COPY = {
  [CapabilityIdentifier.API_KEYS_CREATE]: {
    label: 'API keys',
    preview:
      'Create API keys so your own tools, scripts and dashboards can read accounts and warmup data straight from the TrulyInbox API.',
  },
  [CapabilityIdentifier.TAGS_MANAGE]: {
    label: 'Tags',
    preview:
      'Group mailboxes under your own labels, then filter, report and run bulk actions across a whole tag instead of picking rows by hand.',
  },
  [CapabilityIdentifier.SETTINGS_TEAM_INVITE]: {
    label: 'Team invites',
    preview:
      'Bring teammates into this workspace by email or a shareable link, and give each of them the role that matches what they actually do.',
  },
  [CapabilityIdentifier.DWD_WORKSPACE_CREATE]: {
    label: 'Bulk workspace connect',
    preview:
      'Authorise a whole Google Workspace or Microsoft 365 tenant once, then import hundreds of mailboxes without signing into a single inbox.',
  },
  [CapabilityIdentifier.WARMUP_SETTING_ESP_PRIORITIZATION]: {
    label: 'Custom ESP distribution',
    preview:
      'Set your own Google / Microsoft / Others split so warmup mirrors where your real recipients actually read mail, instead of a fresh random mix each midnight.',
  },
  [CapabilityIdentifier.WARMUP_SETTING_CONTENT_SETTING]: {
    label: 'Custom warmup content',
    preview:
      'Steer what warmup emails talk about by choosing your industry and topics, so the conversations in your inbox read like your real pipeline.',
  },
  [CapabilityIdentifier.WARMUP_SETTING_SENDING_SCHEDULE]: {
    label: 'Custom sending schedule',
    preview:
      'Pick the exact days, hours and volume split warmup sends on, instead of staying on the standard business-hours window.',
  },
  [CapabilityIdentifier.WARMUP_SETTING_WARMUP_LANGUAGE]: {
    label: 'Warmup language',
    preview:
      'Run warmup conversations in the language your prospects actually read, rather than English for every mailbox.',
  },
  [CapabilityIdentifier.WARMUP_SETTING_TEMPLATE_COLLECTION]: {
    label: 'Template collections',
    preview:
      'Draw a share of every warmup send from your own saved templates, so your best-performing copy warms the mailbox alongside AI-generated content.',
  },
  [CapabilityIdentifier.WARMUP_SETTING_REPLY_RATE]: {
    label: 'Reply rate',
    preview:
      'Decide how much of your warmup traffic comes back as replies (the signal mailbox providers weigh most heavily) instead of taking the default for your level.',
  },
  [CapabilityIdentifier.WARMUP_REPORT_DELIVERABILITY_SCORE]: {
    label: 'Deliverability score',
    preview:
      'See a per-provider score for every mailbox, so you know whether it is Gmail, Outlook or the long tail that is filtering you before a campaign goes out.',
  },
  [CapabilityIdentifier.WARMUP_REPORT_ESP_ANALYTICS]: {
    label: 'ESP performance',
    preview:
      'Break inbox placement, opens and replies down by receiving provider over time, and export the day-by-day numbers to share or dig into.',
  },
  [CapabilityIdentifier.EMAIL_ACCOUNT_BULK_CONNECT]: {
    label: 'Bulk CSV connect',
    preview:
      'Upload one spreadsheet of SMTP/IMAP credentials and connect up to 10,000 mailboxes in a single pass, instead of adding them one at a time.',
  },
  [CapabilityIdentifier.WARMUP_REPORT_EXPORT]: {
    label: 'Report export',
    preview:
      'Queue a CSV of warmup metrics across your selected mailboxes and have it emailed to your inbox, instead of reading the numbers one account at a time.',
  },
} as const satisfies Record<CapabilityIdentifier, CapabilityCopy>;
